const ARRAY_GROUPS = [
  'corretores_vendedores',
  'corretores_opcionistas',
  'gerentes',
  'gerentes_gerais',
  'diretores_gerais',
  'repasse_franquias',
  'empresas',
];

export const SHEET_GROUPS = [...ARRAY_GROUPS, 'royalties', 'comunicacao'];

const SINGLE_GROUPS = new Set(['royalties', 'comunicacao']);

export const emptyRow = () => ({
  tipo_pessoa: 'PF',
  porcentagem_real: '0',
  desconto: '0,00',
  bonificacao: '0,00',
  tipo_pagamento: 'pix',
  banco_id: '',
  nome_banco: '',
  agencia: '',
  numero_conta: '',
  tipo_chave_pix_id: '',
  chave_pix: '',
});

const rowsFor = (sheet, group) => {
  const value = sheet?.[group];
  if (SINGLE_GROUPS.has(group))
    return value && Object.keys(value).length ? [value] : [emptyRow()];

  return Array.isArray(value) ? value : [];
};

export const sheetToForm = sheet =>
  SHEET_GROUPS.reduce(
    (form, group) => ({
      ...form,
      [group]: rowsFor(sheet, group).map(row => ({ ...emptyRow(), ...row })),
    }),
    {}
  );

export const formToSheet = form =>
  SHEET_GROUPS.reduce((sheet, group) => {
    const rows = form[group] || [];
    return {
      ...sheet,
      [group]: SINGLE_GROUPS.has(group) ? rows[0] || emptyRow() : rows,
    };
  }, {});

const decimal = value => Number(String(value || 0).replace(',', '.')) || 0;

export const moneyCents = value => {
  if (typeof value === 'number') return Math.round(value);
  const source = String(value || '').replace(/[R$\s]/g, '');
  const normalized = source.includes(',')
    ? source.replace(/\./g, '').replace(',', '.')
    : source;
  return Math.round((Number(normalized) || 0) * 100);
};

export const formatMoney = cents =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format((cents || 0) / 100);

export const rowValueCents = (row, amountCents) =>
  Math.round((amountCents * decimal(row.porcentagem_real)) / 100) -
  moneyCents(row.desconto) +
  moneyCents(row.bonificacao);

export const rowDifferenceCents = row =>
  moneyCents(row.bonificacao) - moneyCents(row.desconto);

export const sheetTotals = (form, amountCents) => {
  const values = SHEET_GROUPS.flatMap(group =>
    (form[group] || []).map(row => ({
      group,
      percentage: decimal(row.porcentagem_real),
      discountCents: moneyCents(row.desconto),
      bonusCents: moneyCents(row.bonificacao),
      amountCents: rowValueCents(row, amountCents),
    }))
  );
  const companyValues = values.filter(value => value.group === 'empresas');
  const nonCompanyValues = values.filter(value => value.group !== 'empresas');
  const percentage = values.reduce(
    (total, value) => total + value.percentage,
    0
  );
  const allocatedAmountCents = values.reduce(
    (total, value) => total + value.amountCents,
    0
  );
  const discountCents = values.reduce(
    (total, value) => total + value.discountCents,
    0
  );
  const bonusCents = values.reduce(
    (total, value) => total + value.bonusCents,
    0
  );
  const companyPercentage = companyValues.reduce(
    (total, value) => total + value.percentage,
    0
  );
  const nonCompanyPercentage = nonCompanyValues.reduce(
    (total, value) => total + value.percentage,
    0
  );
  const companyAmountCents = companyValues.reduce(
    (total, value) => total + value.amountCents,
    0
  );
  const nonCompanyAmountCents = nonCompanyValues.reduce(
    (total, value) => total + value.amountCents,
    0
  );
  const expectedCompanyPercentage = 100 - nonCompanyPercentage;
  const expectedCompanyAmountCents = amountCents - nonCompanyAmountCents;

  return {
    percentage,
    allocatedAmountCents,
    discountCents,
    bonusCents,
    companyPercentage,
    nonCompanyPercentage,
    nonCompanyAmountCents,
    companyAmountCents,
    expectedCompanyPercentage,
    expectedCompanyAmountCents,
    companyMatches:
      Math.abs(companyPercentage - expectedCompanyPercentage) < 0.000001 &&
      companyAmountCents === expectedCompanyAmountCents,
    complete:
      Math.abs(percentage - 100) < 0.000001 &&
      allocatedAmountCents === amountCents,
  };
};
